import {useMemo} from "react";

import {BACKEND_APIS, USER_ROLES, WEBBACK_V2_API} from "@constants";
import {useUserApp} from "@hooks";


export default function useProductPageKpiRoleConfig() {
	const { userRole } = useUserApp()

	// `kpi/context` is served by dipi-webback-v2 (see
	// dipi-webback-v2/plans/869e5pfw2--data-product-page-kpis.md). The MARKETPLACES branch stays on
	// the legacy webback on purpose: v2 does not migrate that role, so `kpi-seller` only exists
	// there.
	const urlApiTable = useMemo(() => ({
		[USER_ROLES.STANDARD]: `${WEBBACK_V2_API}/product-page/kpi/context`,
		[USER_ROLES.RETAILER]: `${WEBBACK_V2_API}/product-page/kpi/context`,
		[USER_ROLES.MARKETPLACES]: `${BACKEND_APIS.PRODUCT_PAGE}/kpi-seller/context`,
	})[userRole], [userRole])

	// `weighted-score` is served by v2: it shares (coalesces) the engine run with `context`, so its
	// marginal cost on screen load is ~0 — the A/B test on 2026-08-19 confirmed the screen time is
	// identical either way, and v2 removes the legacy dependency.
	const urlApiWeighted = useMemo(() => ({
		[USER_ROLES.STANDARD]: `${WEBBACK_V2_API}/product-page/kpi/weighted-score`,
		[USER_ROLES.RETAILER]: `${WEBBACK_V2_API}/product-page/kpi/weighted-score`,
		[USER_ROLES.MARKETPLACES]: `${BACKEND_APIS.PRODUCT_PAGE}/kpi-seller/context`,
	})[userRole], [userRole])

	// `evolution-range` is served by v2 since 2026-08-20, on the same engine as the table. Two things
	// its response does differently, both deliberate and both invisible on screen: one row per day
	// instead of the same row repeated once per configured KPI (v1 appends inside the KPI loop, so a
	// 7-day chart came back with 147 rows), and the day's weighted score is the average of its sites
	// instead of the first site's — which is what makes the chart's last point match the table row
	// the user clicked. MARKETPLACES stays on the legacy backend: v2 does not migrate that role.
	const urlApiEvolution = useMemo(() => ({
		[USER_ROLES.STANDARD]: `${WEBBACK_V2_API}/product-page/kpi/evolution-range`,
		[USER_ROLES.RETAILER]: `${WEBBACK_V2_API}/product-page/kpi/evolution-range`,
		[USER_ROLES.MARKETPLACES]: `${BACKEND_APIS.PRODUCT_PAGE}/kpi-seller/evolution-range`,
	})[userRole], [userRole])

	// `excel` is served by v2 and is the same data as `context` by construction (the service runs the
	// context and formats it). MARKETPLACES stays on the legacy backend: v1 has no `kpi-seller/excel`,
	// so that role has always downloaded the brand export from `kpi/excel` — kept as is rather than
	// pointing a role v2 does not migrate at v2.
	const urlApiExcel = useMemo(() => ({
		[USER_ROLES.STANDARD]: `${WEBBACK_V2_API}/product-page/kpi/excel`,
		[USER_ROLES.RETAILER]: `${WEBBACK_V2_API}/product-page/kpi/excel`,
		[USER_ROLES.MARKETPLACES]: `${BACKEND_APIS.PRODUCT_PAGE}/kpi/excel`,
	})[userRole], [userRole])

	return {
		urlApi: urlApiTable,
		urlApiEvolution: urlApiEvolution,
		urlApiExcel: urlApiExcel,
		urlApiWeighted: urlApiWeighted,
		isMarketplacesUser: userRole === USER_ROLES.MARKETPLACES
	}
}